'use client';

import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import { useBusinessUnitsAll } from '@/hooks/api/useBusinessUnits';
import { BusinessUnitDto } from '@/hooks/api/useAuth';
import { DEFAULT_BUSINESS_UNIT_LOGO_URL } from '@/constants/branding';

function BusinessUnitCard({ bu, onOpen }: { bu: BusinessUnitDto; onOpen: () => void }) {
  const hasWideLogo = Boolean(bu.logoUrl);
  const hasSquare = Boolean(bu.iconUrl);

  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardActionArea
        onClick={onOpen}
        sx={{ height: '100%', display: 'block', textAlign: 'left' }}
      >
        <Box
          sx={{
            minHeight: 72,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 1.5,
            bgcolor: 'action.hover',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {hasWideLogo ? (
            <Box
              component="img"
              src={bu.logoUrl!}
              alt=""
              sx={{
                maxHeight: 64,
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Typography variant="caption" color="text.disabled">
              No wide logo
            </Typography>
          )}
        </Box>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {hasSquare ? (
                <Box
                  component="img"
                  src={bu.iconUrl!}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : hasWideLogo ? (
                <Box
                  component="img"
                  src={bu.logoUrl!}
                  alt=""
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    p: 0.5,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={DEFAULT_BUSINESS_UNIT_LOGO_URL}
                  alt=""
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    p: 0.5,
                    objectFit: 'contain',
                    opacity: 0.4,
                  }}
                />
              )}
            </Box>
            <Box minWidth={0} flex={1}>
              <Typography variant="h6" component="div" noWrap>
                {bu.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {bu.code}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

const BusinessUnitsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: units = [], isLoading, isError } = useBusinessUnitsAll(true);

  return (
    <Box>
      <TitleBar title="Business Units" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open a business unit to load its dashboard. Use the settings icon on the dashboard to
        change the name, wide header logo, or square icon.
      </Typography>
      {isLoading && <CircularProgress size={32} />}
      {isError && (
        <Typography color="error">Unable to load business units.</Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {units.map((bu) => (
          <BusinessUnitCard
            key={bu.id}
            bu={bu}
            onOpen={() => navigate(`/app/business-units/${bu.code}/dashboard`)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default BusinessUnitsPage;
